import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/projects?featured=true`),
          axios.get(`${API}/services`),
        ]);
        setProjects(projectsRes.data.slice(0, 3));
        setServices(servicesRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#F4F4F0]">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center px-6 md:px-12 lg:px-24 pt-24"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1761870065047-f2da9429db23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzIyNjA0NjF8MA&ixlib=rb-4.1.0&q=85"
            alt="Modern architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F4F4F0] via-[#F4F4F0]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            Architecture & Design Studio
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            Designing
            <br />
            Timeless
            <br />
            <span className="text-[#3A4D39]">Spaces</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed mb-10 max-w-lg"
          >
            We create architecture that harmonizes with nature, culture, and the human spirit.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/projects"
              data-testid="hero-view-projects-btn"
              className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#3A4D39] transition-colors duration-300"
            >
              View Projects
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 border border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#1A1A1A] hover:text-[#F4F4F0] transition-colors duration-300"
            >
              Start a Project
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section
        data-testid="featured-projects-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-4">
              Featured Work
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-[#1A1A1A]">
              Selected Projects
            </h2>
          </div>
          <Link
            to="/projects"
            data-testid="view-all-projects-link"
            className="mt-6 md:mt-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/70 hover:text-[#3A4D39] transition-colors"
          >
            View All
            <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* Projects Grid - Tetris Style */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`group relative overflow-hidden ${
                index === 0
                  ? "md:col-span-8 md:row-span-2 aspect-[4/3] md:aspect-auto md:h-[600px]"
                  : "md:col-span-4 aspect-[4/5]"
              }`}
              data-testid={`featured-project-${index}`}
            >
              <img
                src={project.image_url}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/70 mb-2">
                  {project.category}
                </p>
                <h3 className="font-heading text-2xl md:text-3xl text-[#F4F4F0]">
                  {project.title}
                </h3>
                <p className="text-sm text-[#F4F4F0]/70 mt-2">
                  {project.location}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services Overview */}
      <section
        data-testid="services-overview-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#E5E5E0]"
      >
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-4">
            What We Do
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-[#1A1A1A] mb-6">
            Our Services
          </h2>
          <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
            From concept to completion, we offer comprehensive architectural services tailored to your vision.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-8 border border-[#1A1A1A]/10 hover:border-[#1A1A1A] transition-colors duration-500 bg-[#F4F4F0]"
              data-testid={`service-card-${index}`}
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center border border-[#1A1A1A]/20">
                <span className="text-2xl font-heading text-[#3A4D39]">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-3">
                {service.title}
              </h3>
              <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/services"
            data-testid="view-all-services-btn"
            className="inline-flex items-center gap-3 border border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#1A1A1A] hover:text-[#F4F4F0] transition-colors duration-300"
          >
            Learn More
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Stats Section */}
      <section
        data-testid="stats-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {[
            { number: "150+", label: "Projects Completed" },
            { number: "25+", label: "Years Experience" },
            { number: "40+", label: "Awards Won" },
            { number: "15", label: "Team Members" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center md:text-left"
              data-testid={`stat-${index}`}
            >
              <p className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#3A4D39] mb-2">
                {stat.number}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About Teaser */}
      <section
        data-testid="about-teaser-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A1A1A] text-[#F4F4F0]"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/50 mb-6">
              About IIDAS
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light mb-8">
              Architecture that
              <br />
              inspires life.
            </h2>
            <p className="text-lg font-light text-[#F4F4F0]/70 leading-relaxed mb-8">
              For over two decades, IIDAS has been at the forefront of architectural innovation in India. Our philosophy centers on creating spaces that are not just visually stunning but also sustainable and deeply connected to their environment.
            </p>
            <Link
              to="/about"
              data-testid="learn-more-about-btn"
              className="inline-flex items-center gap-3 border border-[#F4F4F0] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#F4F4F0] hover:text-[#1A1A1A] transition-colors duration-300"
            >
              Our Story
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1610650394144-a778795cf585?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjBza2V0Y2hpbmclMjBvbiUyMGJsdWVwcmludCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcyMjYwNDgyfDA&ixlib=rb-4.1.0&q=85"
              alt="Architect sketching"
              className="w-full aspect-[4/5] object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
